/**
 * CricCam Pro — Tournament Bracket & NRR Engine
 * Handles Net Run Rate (NRR) calculations, team standings sorting, and playoff knockout fixtures.
 */

/**
 * Safely convert a value to a finite number
 */
function toNumber(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Calculate Net Run Rate (NRR)
 * NRR = (Total Runs Scored / Overs Faced) - (Total Runs Conceded / Overs Bowled)
 */
export function calculateNetRunRate(stats = {}) {
  const runsScored = toNumber(stats.runsScored);
  const runsConceded = toNumber(stats.runsConceded);

  // ICC tournament rule: a side dismissed before using its full allocation is
  // charged its FULL allotted overs, not the balls it actually faced. Without
  // this a team bowled out for 50 in 6.2 overs gets a wildly inflated NRR.
  const allottedBalls = toNumber(stats.allottedBalls);
  const rawFaced = toNumber(stats.ballsFaced);
  const rawBowled = toNumber(stats.ballsBowled);
  const oppAllottedBalls = toNumber(stats.oppAllottedBalls) || allottedBalls;

  const ballsFaced = stats.wasAllOut && allottedBalls > 0 ? allottedBalls : rawFaced;
  const ballsBowled = stats.oppAllOut && oppAllottedBalls > 0 ? oppAllottedBalls : rawBowled;

  const oversFaced = ballsFaced > 0 ? ballsFaced / 6 : 0;
  const oversBowled = ballsBowled > 0 ? ballsBowled / 6 : 0;

  const scoredRate = oversFaced > 0 ? runsScored / oversFaced : 0;
  const concededRate = oversBowled > 0 ? runsConceded / oversBowled : 0;

  // Return the raw float. Rounding to 3dp HERE destroyed precision before the
  // standings comparison ever ran; formatting belongs at the render layer.
  return scoredRate - concededRate;
}

/**
 * Display helper — NRR is conventionally shown to 3 decimal places.
 */
export function formatNetRunRate(nrr) {
  const n = Number(nrr);
  return Number.isFinite(n) ? n.toFixed(3) : "0.000";
}

/**
 * Head-to-head record between two teams across completed fixtures.
 * Returns a comparator value: negative if `a` should rank above `b`.
 */
export function headToHeadResult(a, b, fixtures = []) {
  if (!Array.isArray(fixtures) || !a || !b) return 0;
  let aWins = 0, bWins = 0;
  for (const f of fixtures) {
    const winner = f?.winner || f?.result?.winnerName;
    if (!winner) continue;
    const teams = [f.teamA, f.teamB, f.homeTeam, f.awayTeam].filter(Boolean);
    if (!teams.includes(a) || !teams.includes(b)) continue;
    if (winner === a) aWins++;
    else if (winner === b) bWins++;
  }
  if (aWins === bWins) return 0;
  return aWins > bWins ? -1 : 1;
}

/**
 * Sort tournament standings by Points -> Wins -> NRR -> Total Runs Scored -> Team Name
 */
export function sortTournamentStandings(pointsTable = {}, fixtures = []) {
  if (!pointsTable || typeof pointsTable !== 'object') return [];

  const teams = Object.keys(pointsTable).map(teamName => {
    const s = pointsTable[teamName] || {};
    const nrr = calculateNetRunRate(s);
    const won = toNumber(s.won);
    const lost = toNumber(s.lost);
    const tied = toNumber(s.tied);
    const noResult = toNumber(s.noResult);

    const matches = s.matches != null ? toNumber(s.matches) : (won + lost + tied + noResult);

    return {
      teamName: String(teamName).trim(),
      matches,
      won,
      lost,
      tied,
      noResult,
      pts: toNumber(s.pts),
      runsScored: toNumber(s.runsScored),
      ballsFaced: toNumber(s.ballsFaced),
      runsConceded: toNumber(s.runsConceded),
      ballsBowled: toNumber(s.ballsBowled),
      nrr
    };
  });

  return teams.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.won !== a.won) return b.won - a.won;
    if (Math.abs(b.nrr - a.nrr) > 1e-9) return b.nrr - a.nrr;
    const tiedCount = teams.filter(t => t.pts === a.pts && t.won === a.won && Math.abs(t.nrr - a.nrr) <= 1e-9).length;
    if (tiedCount === 2) {
      const h2h = headToHeadResult(a.teamName, b.teamName, fixtures);
      if (h2h !== 0) return h2h;
    }
    if (b.runsScored !== a.runsScored) return b.runsScored - a.runsScored;
    return a.teamName.localeCompare(b.teamName);
  });
}

/**
 * Generate IPL-style Playoff Knockout Fixtures for Top 4 Teams
 */
export function generateKnockoutFixtures(standings = []) {
  if (!Array.isArray(standings) || standings.length < 2) return [];

  const top4 = standings.slice(0, 4);
  const fixtures = [];

  if (top4.length >= 4) {
    // IPL Playoff format
    fixtures.push({
      id: 'Q1',
      title: 'Qualifier 1',
      teamA: top4[0].teamName,
      teamB: top4[1].teamName,
      badge: 'TOP 2 ADVANTAGE',
      description: 'Winner directly reaches Final'
    });

    fixtures.push({
      id: 'ELIM',
      title: 'Eliminator',
      teamA: top4[2].teamName,
      teamB: top4[3].teamName,
      badge: 'KNOCKOUT',
      description: 'Loser is eliminated'
    });

    fixtures.push({
      id: 'Q2',
      title: 'Qualifier 2',
      teamA: `Loser of Q1 (${top4[0].teamName} / ${top4[1].teamName})`,
      teamB: `Winner of Eliminator (${top4[2].teamName} / ${top4[3].teamName})`,
      badge: 'SEMI-FINAL',
      description: 'Winner reaches Final'
    });

    fixtures.push({
      id: 'FINAL',
      title: 'GRAND FINAL',
      teamA: `Winner of Q1`,
      teamB: `Winner of Q2`,
      badge: 'CHAMPIONSHIP',
      description: 'Championship Decider'
    });
  } else if (top4.length === 3) {
    // 3 teams: the BYE belongs to the table-topper. The old version put #1 v #2
    // in the semi and handed #3 — the lowest-ranked side — a free pass to the
    // final, penalising the two best teams.
    fixtures.push({
      id: 'SF1',
      title: 'Semi Final',
      teamA: top4[1].teamName,
      teamB: top4[2].teamName,
      badge: 'KNOCKOUT',
      description: 'Winner reaches Final'
    });
    fixtures.push({
      id: 'FINAL',
      title: 'GRAND FINAL',
      teamA: top4[0].teamName,
      teamB: `Winner of Semi Final`,
      badge: 'CHAMPIONSHIP',
      description: 'Championship Decider — #1 seed has the bye'
    });
  } else {
    // 2 teams: straight final.
    fixtures.push({
      id: 'FINAL',
      title: 'GRAND FINAL',
      teamA: top4[0].teamName,
      teamB: top4[1].teamName,
      badge: 'CHAMPIONSHIP',
      description: 'Championship Decider'
    });
  }

  return fixtures;
}
